import { parseISO } from 'date-fns';
import { format, fromZonedTime, toZonedTime } from 'date-fns-tz';

import { DATE_FORMATS } from '../constants';
import { ALL_COUNTRIES_TIMEZONES, DEFAULT_TIMEZONE } from '../constants/timezones';
import type { DateInput } from '../types';

export type TimezoneOption = (typeof ALL_COUNTRIES_TIMEZONES)[number] & {
  currentTime: string;
};

/**
 * Converts a date from one timezone to another.
 *
 * @param date - The input date.
 * @param fromTimezone - The source timezone).
 * @param toTimezone - The target timezone.
 * @returns A Date object representing the converted time in the target timezone.
 */
export function convertTimezone(date: DateInput, fromTimezone: string, toTimezone: string): Date {
  const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);

  // If the input is already a UTC date (has 'Z' suffix or is a Date object),  we need to handle it differently
  if (fromTimezone === 'UTC') {
    // For UTC input, we can directly convert to the target timezone
    return toZonedTime(dateObj, toTimezone);
  }

  const utcDate = fromZonedTime(dateObj, fromTimezone);
  return toZonedTime(utcDate, toTimezone);
}

/**
 * Formats a date in a given timezone using a custom format string.
 *
 * @param date - The date to format.
 * @param timezone - The timezone to format the date in.
 * @param formatStr - The desired date format string (e.g., 'h:mm a').
 * @returns A formatted string representing the date in the specified timezone.
 */
export function formatInTimezone(date: DateInput, timezone: string, formatStr: string): string {
  const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);
  return format(dateObj, formatStr, { timeZone: timezone });
}

/**
 * Parses a date string assuming it originates in a specific timezone.
 *
 * @param dateString - A date string.
 * @param timezone - The timezone the date string is assumed to be in.
 * @returns A Date object adjusted to UTC based on the source timezone.
 */
export function parseInTimezone(dateString: string, timezone: string): Date {
  const date = parseISO(dateString);
  return fromZonedTime(date, timezone);
}

/**
 * Retrieves the user's current timezone.
 *
 * @returns A timezone string, or a default fallback ('UTC').
 */
export function getUserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TIMEZONE;
  } catch {
    return DEFAULT_TIMEZONE;
  }
}

/**
 * Calculates the timezone offset (in hours) from UTC for a given timezone and date.
 *
 * @param timezone - The timezone identifier.
 * @param date - The date to evaluate offset for (defaults to now).
 * @returns The timezone offset in hours relative to UTC.
 */
export function getTimezoneOffset(timezone: string, date = new Date()): number {
  const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);

  const tzDate = toZonedTime(dateObj, timezone);
  const utcDate = toZonedTime(dateObj, 'UTC');

  return (tzDate.getTime() - utcDate.getTime()) / (1000 * 60 * 60);
}

/**
 * Converts a UTC date into the user's local timezone.
 *
 * @param date - The date to convert.
 * @returns A Date object in the user's current timezone.
 */
export function toLocalTimezone(date: DateInput): Date {
  const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);
  const userTimezone = getUserTimezone();
  return toZonedTime(dateObj, userTimezone);
}

/**
 * Converts a local date to UTC, assuming a specific source timezone.
 *
 * @param date - The local date to convert.
 * @param timezone - The source timezone (defaults to user's timezone).
 * @returns A Date object in UTC.
 */
export function toUTC(date: DateInput, timezone = getUserTimezone()): Date {
  const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);

  // Use the same approach as convertTimezone but from the specified timezone to UTC
  return convertTimezone(dateObj, timezone, 'UTC');
}

/**
 * Generates a list of common timezones for UI dropdowns,
 * including current local time in each timezone.
 *
 * @returns An array of timezone options with `value`, `label`, `offset`, and `currentTime`.
 */
export function getTimezoneList(): TimezoneOption[] {
  return ALL_COUNTRIES_TIMEZONES.map(tz => ({
    ...tz,
    currentTime: formatInTimezone(new Date(), tz.value, DATE_FORMATS.TIME_DISPLAY),
  }));
}

/**
 * Checks whether a given timezone string is valid.
 *
 * @param timezone - The timezone string to validate.
 * @returns `true` if valid, otherwise `false`.
 */
export function isValidTimezone(timezone: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
}
